import React, { useEffect, useState } from "react";
import { ArrowRight, Instagram, Facebook, Youtube, Globe } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSettings, resolveImage } from "../lib/settings";
import { Link } from "react-router-dom";
import { fetchPages } from "../lib/api";
import { T } from "../lib/autoTranslate";

const CTA = () => {
  const { t } = useI18n();
  return (
    <section id="book" className="section bg-[var(--ri-cream-2)] relative overflow-hidden">
      <div className="container-x text-center max-w-[720px] relative z-10">
        <h2 className="font-serif-display text-[38px] md:text-[54px] leading-[1.05]">{t("cta_title")}</h2>
        <p className="mt-6 text-[17px] leading-[1.75] text-[var(--ri-ink-soft)]">{t("cta_sub")}</p>
        <div className="mt-8 inline-flex">
          <Link to="/tours" className="btn-primary inline-flex items-center gap-2">
            {t("choose_your_tour")} <ArrowRight size={16} />
          </Link>
        </div>
        <p className="mt-5 text-[13px] text-[var(--ri-muted)]">
          {t("free_cancellation")} · {t("no_booking_fees")}
        </p>
      </div>
    </section>
  );
};

const TripadvisorIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="7.2" cy="13" r="3.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="16.8" cy="13" r="3.6" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="7.2" cy="13" r="1.3" fill="currentColor" />
    <circle cx="16.8" cy="13" r="1.3" fill="currentColor" />
    <path d="M12 5.2c-2.1 0-4.4.55-6.3 1.7 1.2-.35 2.6-.55 4-.55m2.3-1.15c2.1 0 4.4.55 6.3 1.7-1.2-.35-2.6-.55-4-.55" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M9.6 5.9C11 5.35 12.9 5.35 14.3 5.9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const Footer = () => {
  const { t } = useI18n();
  const { settings } = useSettings();
  const s = settings || {};
  const [footerPages, setFooterPages] = useState([]);

  useEffect(() => {
    fetchPages()
      .then((data) => setFooterPages((data || []).filter((p) => p.show_in_footer)))
      .catch(() => setFooterPages([]));
  }, []);

  return (
    <footer id="contact" className="bg-[#1a1613] text-[#e6ddc9]">
      <div className="container-x pt-20 pb-10">
        <div className="grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <span className="brand-script text-[30px] text-[#ff8a70]">Real</span>
              <span className="font-serif-display text-[22px] text-white">Istanbul</span>
              <span className="brand-script text-[26px] text-[#ff8a70]">Tours</span>
            </div>
            <p className="mt-5 max-w-[420px] text-[15px] leading-[1.75] text-[#c8bfaa]"><T>{s.footer_tagline || t("footer_tagline")}</T></p>

            {s.footer_logos && s.footer_logos.length > 0 && (
              <div className="mt-6 flex flex-wrap items-start gap-x-8 gap-y-5">
                {s.footer_logos.map((item, i) => {
                  const Wrapper = item.link ? "a" : "div";
                  const wrapperProps = item.link
                    ? { href: item.link, target: "_blank", rel: "noopener noreferrer" }
                    : {};
                  return item.image ? (
                    <Wrapper key={i} {...wrapperProps} className="flex flex-col items-start gap-2.5 hover:opacity-80 transition-opacity">
                      <div className="w-28 h-28 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                        <img
                          src={resolveImage(item.image)}
                          alt={item.text || "Logo"}
                          loading="lazy"
                          className="max-w-[96px] max-h-[96px] w-auto h-auto object-contain"
                        />
                      </div>
                      {item.text && <span className="text-[13px] text-[#c8bfaa]">{item.text}</span>}
                    </Wrapper>
                  ) : (
                    <Wrapper key={i} {...wrapperProps} className="flex items-center hover:opacity-80 transition-opacity">
                      {item.text && <span className="text-[13px] text-[#c8bfaa]">{item.text}</span>}
                    </Wrapper>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <p className="eyebrow text-[#ff8a70]">Tours</p>
            <ul className="mt-4 space-y-2.5 text-[14.5px] text-[#c8bfaa]">
              <li><Link to="/tours" className="hover:text-white"><T>All Tours</T></Link></li>
              <li><Link to="/tours/small-group" className="hover:text-white"><T>Small Group Tours</T></Link></li>
              <li><Link to="/tours/private" className="hover:text-white"><T>Private Tours</T></Link></li>
              <li><Link to="/tours/cooking" className="hover:text-white"><T>Cooking Classes</T></Link></li>
              <li><Link to="/tours/workshops" className="hover:text-white"><T>Workshops</T></Link></li>
              <li><Link to="/tours/day-trips" className="hover:text-white"><T>Day Trips</T></Link></li>
            </ul>
          </div>

          <div>
            <p className="eyebrow text-[#ff8a70]"><T>Company</T></p>
            <ul className="mt-4 space-y-2.5 text-[14.5px] text-[#c8bfaa]">
              <li><Link to="/about" className="hover:text-white"><T>About us</T></Link></li>
              <li><Link to="/blog" className="hover:text-white"><T>Blog</T></Link></li>
              <li><Link to="/faq" className="hover:text-white"><T>FAQ</T></Link></li>
              <li><Link to="/contact" className="hover:text-white"><T>Contact</T></Link></li>
              <li><Link to="/privacy-policy" className="hover:text-white"><T>Privacy Policy</T></Link></li>
              <li><Link to="/terms-conditions" className="hover:text-white"><T>Terms & Conditions</T></Link></li>
              {footerPages.map((p) => (
                <li key={p.slug}>
                  <Link to={`/page/${p.slug}`} className="hover:text-white"><T>{p.nav_label || p.title}</T></Link>
                </li>
              ))}
              <li><Link to="/admin/login" className="hover:text-white text-[13px] opacity-70">Admin</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <p className="text-[13px] text-[#8a7f6b]">
            © {new Date().getFullYear()} Real Istanbul Tours · realistanbultours.com · Istanbul, Turkey
          </p>
          <div className="flex items-center gap-4 text-[#c8bfaa]">
            <a href={s.social_instagram_url || "https://www.instagram.com/realistanbultours/"} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-white"><Instagram size={18} /></a>
            <a href={s.social_facebook_url || "https://www.facebook.com/realistanbultoursturkey/"} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-white"><Facebook size={18} /></a>
            {s.social_youtube_url ? (
              <a href={s.social_youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-white"><Youtube size={18} /></a>
            ) : (
              <span aria-label="YouTube" title="Add your YouTube link in Site Editor" className="opacity-40 cursor-default"><Youtube size={18} /></span>
            )}
            <a href={s.social_tripadvisor_url || "https://www.tripadvisor.com/Attraction_Review-g293974-d3547695-Reviews-Real_Istanbul_Tours-Istanbul.html"} target="_blank" rel="noopener noreferrer" aria-label="Tripadvisor" className="hover:text-white" title="Tripadvisor"><TripadvisorIcon size={18} /></a>
            {s.social_viator_url ? (
              <a href={s.social_viator_url} target="_blank" rel="noopener noreferrer" aria-label="Viator" className="hover:text-white" title="Viator"><Globe size={18} /></a>
            ) : (
              <span aria-label="Viator" title="Add your Viator link in Site Editor" className="opacity-40 cursor-default"><Globe size={18} /></span>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export { CTA };
export default Footer;
